import { styled } from '@mui/material';

import { ReactNode } from 'react';

import { ArrowForwardIcon } from '@app-velocity/icons';
import { useI18n } from '@app-velocity/i18n';
import { Tooltip } from '@app-velocity/ui';

import { usePathScreensScroll } from './hooks/usePathScreensScroll';

const CarouselWrapper = styled('div')({
  position: 'relative',
  width: '100%',
});

const ScrollFade = styled('div', {
  shouldForwardProp: (prop) => prop !== 'side',
})<{ side: 'left' | 'right' }>(({ theme, side }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  height: 'calc(100% - 1px)',
  [side]: 0,
  width: theme.spacing(25),
  pointerEvents: 'none',
  zIndex: 1,
  background:
    side === 'left'
      ? 'linear-gradient(to right, var(--background-background-color, #fff) 0%, transparent 100%)'
      : 'linear-gradient(to left, var(--background-background-color, #fff) 0%, transparent 100%)',
}));

const ScrollArea = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  flexWrap: 'nowrap',
  gap: theme.spacing(5),
  overflow: 'auto',
  padding: theme.spacing(4),
  width: '100%',
  cursor: 'grab',
  userSelect: 'none',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '& img, & canvas': {
    pointerEvents: 'none',
    userSelect: 'none',
    WebkitUserDrag: 'none',
  },
  '& a, & button': {
    userSelect: 'none',
    WebkitUserDrag: 'none',
  },
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

export const OuterSlideContainer = styled('div')(({ theme }) => ({
  minWidth: theme.spacing(50),
  flex: '0 0 auto',
}));

const NavButtonSlot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'side',
})<{ side: 'left' | 'right' }>(({ side }) => ({
  position: 'absolute',
  top: '50%',
  [side]: 8,
  transform: 'translateY(-50%)',
  zIndex: 2,
  display: 'flex',
}));

const NavButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'rotated',
})<{ rotated?: boolean }>(({ theme, rotated }) => ({
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transform: rotated ? 'rotate(180deg)' : undefined,
  borderRadius: '50%',
  background: 'rgba(0,0,0,0.7)',
  width: theme.spacing(8),
  height: theme.spacing(8),
  padding: 0,
  svg: {
    fill: '#fff',
    width: 20,
    height: 20,
  },
}));

export type PathScreensScrollProps = {
  itemCount: number;
  children: (helpers: {
    getItemRef: (index: number) => (element: HTMLDivElement | null) => void;
    shouldIgnoreClick: () => boolean;
  }) => ReactNode;
};

export const PathScreensScroll = ({
  itemCount,
  children,
}: PathScreensScrollProps) => {
  const { t } = useI18n();
  const {
    scrollRef,
    getItemRef,
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext,
    shouldIgnoreClick,
    scrollHandlers,
  } = usePathScreensScroll(itemCount);

  return (
    <CarouselWrapper>
      {canScrollPrev ? <ScrollFade side="left" /> : null}
      {canScrollNext ? <ScrollFade side="right" /> : null}
      {canScrollPrev ? (
        <NavButtonSlot side="left">
          <Tooltip
            title={t('prototypes:report.paths.lightbox.previous_screen')}
          >
            <NavButton type="button" rotated onClick={scrollPrev}>
              <ArrowForwardIcon />
            </NavButton>
          </Tooltip>
        </NavButtonSlot>
      ) : null}
      {canScrollNext ? (
        <NavButtonSlot side="right">
          <Tooltip title={t('prototypes:report.paths.lightbox.next_screen')}>
            <NavButton type="button" onClick={scrollNext}>
              <ArrowForwardIcon />
            </NavButton>
          </Tooltip>
        </NavButtonSlot>
      ) : null}
      <ScrollArea
        ref={scrollRef}
        onDragStart={(event) => event.preventDefault()}
        {...scrollHandlers}
      >
        {children({ getItemRef, shouldIgnoreClick })}
      </ScrollArea>
    </CarouselWrapper>
  );
};
